import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Sparkles, X, Check } from "lucide-react";
import { api } from "@/lib/api";
import { APP_VERSION } from "@/lib/version";

const SEEN_KEY = "bloq_changelog_seen";

// Dismissible "What's new" card — shows the latest published changelog to users once per version.
export function WhatsNewCard() {
  const { data } = useQuery({
    queryKey: ["changelog-latest", APP_VERSION],
    queryFn: () => api.latestChangelog(APP_VERSION),
    staleTime: 5 * 60 * 1000,
  });
  const latest = data?.latest;
  const [seen, setSeen] = useState(() => localStorage.getItem(SEEN_KEY));

  if (!latest?.id || seen === latest.id) return null;

  const dismiss = () => {
    localStorage.setItem(SEEN_KEY, latest.id);
    setSeen(latest.id);
  };

  const items = (latest.items || []).slice(0, 3);

  return (
    <div
      className="mb-2 rounded-2xl border border-bloq-purple/25 bg-bloq-purple/[0.08] p-4"
      data-testid="whats-new-card"
    >
      <div className="flex items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-bloq-purple/20 text-bloq-purple">
          <Sparkles className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            {latest.version && (
              <span className="rounded-md border border-bloq-purple/40 bg-bloq-purple/15 px-1.5 py-0.5 text-[10px] font-bold text-bloq-purple">
                v{latest.version}
              </span>
            )}
            <span className="truncate text-sm font-semibold" data-testid="whats-new-title">
              {latest.title}
            </span>
          </div>
          {items.length > 0 && (
            <ul className="mt-2 space-y-1">
              {items.map((it, i) => (
                <li key={i} className="flex items-start gap-2 text-xs text-white/80">
                  <Check className="mt-0.5 h-3 w-3 shrink-0 text-bloq-mint" />
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          )}
          <button
            onClick={dismiss}
            className="mt-3 text-xs font-semibold text-bloq-mint hover:opacity-80"
            data-testid="whats-new-dismiss"
          >
            Got it
          </button>
        </div>
        <button
          onClick={dismiss}
          className="shrink-0 rounded-full p-1 text-bloq-muted hover:text-white"
          data-testid="whats-new-close"
          title="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
